<template>
  <div
    v-if="loggedInUser"
    class="user-navigation"
    :class="{ '-open': isDropdownOpen }"
  >
    <div class="topbar">
      <template v-if="showGlobalWarnings">
        <GlobalNotification v-if="licence?.isTrialVersion" class="notification">
          <RouterLink :to="{ name: 'LicenceView' }">{{
            t('cockpit.licence.trial.remainingDays', {
              days: daysLeftUntil(new Date(licence?.validUntil)),
            })
          }}</RouterLink>
        </GlobalNotification>
        <GlobalNotification
          v-else-if="showFutureLicenceWarning"
          class="notification"
        >
          <RouterLink :to="{ name: 'LicenceView' }">{{
            t('cockpit.licence.info.notEnoughLicencesForNextYear')
          }}</RouterLink>
        </GlobalNotification>
        <ButtonTextLink
          v-else-if="route.name !== 'LicenceView'"
          class="notification"
          :label="t('cockpit.licence.info.buyMore')"
          :to="{ name: 'LicenceView' }"
        />
      </template>
      <template v-else-if="isAccessingSharedAccount && accountOwnerUser">
        <small style="color: var(--color-stone-400); margin-right: 0.5rem">
          {{
            [accountOwnerUser.firstname, accountOwnerUser.lastname].join(' ')
          }}
        </small>
      </template>

      <button
        type="button"
        :class="{ '-active': isOnProfileView || isOnLicenceView }"
        class="navlink nav-link-icon"
        tabindex="0"
        @focusin="toggleDropdown(true)"
        @focusout="toggleIfActive()"
      >
        <component :is="heroIcons['user-circle']" class="icon" />
        <span class="text">{{ loggedInUser.firstname }}</span>
      </button>
    </div>
    <div class="dropdown" :class="{ '-open': isDropdownOpen }">
      <ul ref="dropdownInnerElement" class="inner">
        <li
          v-if="!isAccessingSharedAccount"
          ref="profileOverviewElement"
          class="item"
          :class="{ '-active': isOnProfileView }"
          @click="toggleDropdown(false)"
        >
          <RouterLink
            class="link"
            :to="{ name: 'ProfileOverview' }"
            :aria-disabled="isOnProfileView"
            tabindex="-1"
          >
            {{ t('cockpit.navigationBar.userInfos.accountSettings') }}
          </RouterLink>
        </li>
        <li
          class="item"
          :class="{ '-active': isOnLicenceView }"
          @click="toggleDropdown(false)"
        >
          <RouterLink
            class="link"
            :to="{ name: 'LicenceView' }"
            :aria-disabled="isOnLicenceView"
            tabindex="-1"
          >
            {{ t('cockpit.navigationBar.userInfos.licence') }}
          </RouterLink>
        </li>
        <li
          v-if="!isAccessingSharedAccount"
          class="item"
          :class="{ '-active': isOnShareCockpitView }"
          @click="toggleDropdown(false)"
        >
          <RouterLink
            class="link"
            :to="{ name: 'ShareCockpit' }"
            :aria-disabled="isOnShareCockpitView"
            tabindex="-1"
          >
            {{ t('cockpit.navigationBar.userInfos.share') }}
          </RouterLink>
        </li>
        <li
          v-if="loggedInUser.accessTo.length > 0"
          class="item"
          :class="{ '-active': isOnSwitchCockpitView }"
          @click="toggleDropdown(false)"
        >
          <RouterLink
            class="link"
            :to="{ name: 'SwitchCockpit' }"
            :aria-disabled="isOnSwitchCockpitView"
            tabindex="-1"
          >
            {{ t('cockpit.navigationBar.userInfos.switch') }}
          </RouterLink>
        </li>
        <li ref="logoutElement" class="item">
          <form class="title logout-form" action="/auth/logout" method="POST">
            <input type="hidden" name="_token" :value="csrfToken" />
            <button tabindex="-1" type="submit" class="button">
              {{ t('cockpit.navigationBar.userInfos.signOut') }}
            </button>
          </form>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { injectStrict } from '~/common/inject';
import { AppDataInjectionKey } from '~/common/useAppData';
import useCsrf from '~/common/forms/useCsrf';
import { useI18n } from 'vue-i18n';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { heroIcons } from '~/common/icons/heroIcons';
import { onKeyStroke } from '@vueuse/core';
import { daysLeftUntil } from '@shared/date-util';
import GlobalNotification from '~/common/GlobalNotification.vue';
import { useLicenceCalculations } from '~/common/useLicenceCalculations';
import ButtonTextLink from '~/common/buttons/ButtonTextLink.vue';
import { useSharedUser } from '~/cockpit/sharing/useSharedUser';

const { t } = useI18n();
// const { data: appData } = injectStrict(AppDataInjectionKey);
const {
  showGlobalWarnings,
  isAccessingSharedAccount,
  accountOwnerUser,
  loggedInUser,
} = useSharedUser();
const { token: csrfToken } = useCsrf();
const route = useRoute();

const isDropdownOpen = ref<boolean>(false);
const profileOverviewElement = ref<HTMLElement | null>(null);
const logoutElement = ref<HTMLElement | null>(null);
const optionsElements = ref<HTMLElement[]>([]);
const focusedOption = ref<number>(-1);
const dropdownInnerElement = ref<HTMLElement | null>(null);

const { licence } = injectStrict(AppDataInjectionKey);

function toggleDropdown(open = !isDropdownOpen.value) {
  isDropdownOpen.value = open;
  focusedOption.value = -1;
}

function toggleIfActive() {
  if (focusedOption.value !== -1) {
    return;
  }
  toggleDropdown(false);
}

const isOnLicenceView = computed(() => {
  return route.name === 'LicenceView';
});

const isOnProfileView = computed(() => {
  return route.name === 'ProfileOverview';
});

const isOnShareCockpitView = computed(() => {
  return route.name === 'ShareCockpit';
});

const isOnSwitchCockpitView = computed(() => {
  return route.name === 'SwitchCockpit';
});

watch(
  () => route.name,
  () => {
    toggleDropdown(false);
  },
);

watch(focusedOption, (newVal) => {
  if (optionsElements.value.length >= 1 && newVal !== -1) {
    optionsElements.value[newVal]?.focus();
  }
});

onMounted(() => {
  addArrowKeydownListener();
});

function addArrowKeydownListener() {
  getInnerElements();

  onKeyStroke('ArrowDown', (e) => {
    if (isDropdownOpen.value) {
      e.preventDefault();
      focusedOption.value =
        focusedOption.value === optionsElements?.value?.length - 1
          ? 0
          : focusedOption.value + 1;
    }
  });

  onKeyStroke('ArrowUp', (e) => {
    if (isDropdownOpen.value) {
      e.preventDefault();
      focusedOption.value =
        focusedOption.value === 0
          ? optionsElements?.value?.length - 1
          : focusedOption.value - 1;
    }
  });

  onKeyStroke('Escape', () => {
    if (isDropdownOpen.value) {
      toggleDropdown(false);
    }
  });
}

function getInnerElements() {
  if (dropdownInnerElement.value) {
    optionsElements.value = Array.from(
      dropdownInnerElement.value.querySelectorAll('.link, .button'),
    ) as HTMLElement[];
  }
}

const { shouldWarnDueToFutureLicenceHavingLowerStudentLimit } =
  useLicenceCalculations();
const showFutureLicenceWarning = computed(() =>
  shouldWarnDueToFutureLicenceHavingLowerStudentLimit(),
);
</script>

<style scoped lang="scss">
.user-navigation {
  position: relative;
  z-index: 10;

  &:hover:not(:has(.notification:hover)),
  &.-open {
    > .dropdown {
      visibility: visible;
      clip-path: inset(0 0 0 0);
    }

    > .topbar {
      > .navlink {
        background-color: var(--color-gull-gray-400);
      }
    }
  }

  > .topbar {
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    z-index: 10;

    > .navlink {
      z-index: 10;
    }

    > .notification {
      @include text-sm;

      margin-right: 1rem;
    }
  }

  > .dropdown {
    position: absolute;
    top: 0;
    right: 0;
    visibility: hidden;
    transition: clip-path var(--base-transition-duration-slow);
    clip-path: inset(0 0 100% 0);

    > .inner {
      margin-top: 2.75rem;
      border-radius: 0.5rem;
      border: 2px solid var(--color-gull-gray-950-opacity-25);
      overflow: clip;

      > .item {
        min-width: 15rem;
        cursor: pointer;
        background-color: var(--color-white);
        transition: background-color 500ms;
        user-select: none;

        &.-active {
          background-color: var(--color-gull-gray-100);
        }

        &:not(:last-of-type) {
          border-bottom: 2px solid var(--color-gull-gray-950-opacity-25);
        }

        &:hover {
          background-color: var(--color-gull-gray-100);
        }

        &:focus,
        &:focus-within {
          // background-color: var(--color-white);
          background-color: var(--color-gull-gray-100);
        }

        > .link {
          @include text-md();

          width: 100%;
          padding: 0.925rem 1rem;
          display: inline-block;
          outline: none;
          color: var(--color-gull-gray-950);
        }
      }
    }
  }
}

.logout-form {
  width: 100%;
  height: 100%;

  > .button {
    width: 100%;
    height: 100%;
    padding: 0.925rem 1rem;
    background-color: transparent;
    text-align: left;
    border: none;
    outline: none;
    cursor: pointer;
    color: var(--color-gull-gray-950);
  }
}
</style>
